import React from 'react';
import { ArrowsLeftRight, SealCheck, SealWarning, Signature, Users, WarningCircle } from '@phosphor-icons/react';
import { AnteilRing } from '../../../../components/immobilien/ui';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import type { DlAblauf, DlKennzahlen } from '../../../../lib/dienstleister/liste';

const ABLAUF_FARBE: Record<DlAblauf['status'], { fg: string; bg: string }> = {
  abgelaufen: { fg: 'var(--bad)', bg: 'var(--bad-soft)' },
  laeuft_ab: { fg: 'var(--warn)', bg: 'var(--warn-soft)' },
  gueltig: { fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  vertrag: { fg: 'var(--info)', bg: 'var(--info-soft)' },
};

/** Widgets der Dienstleisterliste (Handoff Dienstleister §6, Screens 01/02/36). */
export function DienstleisterWidgets({ k, ablaeufe, onAktiv, onOffen, onDienstleister }: {
  k: DlKennzahlen;
  ablaeufe: readonly (DlAblauf & { id: string; name: string })[];
  onAktiv: () => void;
  onOffen: () => void;
  onDienstleister: (id: string) => void;
}) {
  const tastatur = (aktion: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aktion(); }
  };
  return (
    <section aria-label="Übersicht Dienstleister" className="flex flex-col" style={{ gap: 14 }}>
      <div role="button" tabIndex={0} aria-label="Aktive Dienstleister anzeigen" onClick={onAktiv} onKeyDown={tastatur(onAktiv)}
        className="immo-akzent relative flex flex-col overflow-hidden cursor-pointer" style={{ minHeight: 150, borderRadius: 26, padding: 18 }}>
        <Users weight="fill" size={150} aria-hidden className="absolute pointer-events-none" style={{ right: -14, bottom: -26, opacity: 0.12 }} />
        <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700 }}><Users weight="fill" size={15} />Aktive Dienstleister</span>
        <div className="flex items-end" style={{ marginTop: 'auto', gap: 16 }}>
          <span className="immo-zahl" style={{ fontSize: 58, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.95 }}>{k.aktiv}</span>
          <div className="flex flex-col" style={{ gap: 2, paddingBottom: 4 }}>
            <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700 }}>{eur(k.volumen, { nachkomma: 0 })} Auftragsvolumen {new Date().getFullYear()}</span>
            <span style={{ fontSize: 12, color: 'var(--acc-ink2)' }}>{k.notfall} Notfall 24/7 · {k.bevorzugt} bevorzugt · {k.angebot} erst Angebot</span>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gridAutoRows: 'minmax(150px, auto)', gap: 14 }}>
        <div role="button" tabIndex={0} aria-label="Dienstleister mit offenen Rechnungen anzeigen" onClick={onOffen} onKeyDown={tastatur(onOffen)}
          className="immo-glas immo-hover-widget flex flex-col cursor-pointer" style={{ borderRadius: 26, padding: 16, gap: 6 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--bad)' }}><WarningCircle weight="fill" size={15} />Offene Rechnungen</span>
          <div className="flex flex-col" style={{ marginTop: 'auto', gap: 2 }}>
            <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: k.offen > 0.005 ? 'var(--bad)' : 'var(--ok)' }}>
              {k.offen > 0.005 ? eur(-k.offen, { nachkomma: 0 }) : 'Alle bezahlt'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink2)' }}>{k.mitOffen} Dienstleister mit offenen Rechnungen</span>
          </div>
        </div>

        <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 16, gap: 6 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><SealCheck weight="fill" size={15} />Nachweise</span>
          <div className="flex flex-1 items-center" style={{ gap: 12 }}>
            <AnteilRing prozent={k.nachweisGueltig + k.nachweisLaeuftAb === 0 ? null : k.nachweisQuote} farbe="var(--ok)" text={k.nachweisGueltig + k.nachweisLaeuftAb === 0 ? '–' : undefined} />
            <div className="flex flex-col" style={{ gap: 2, fontSize: 12, color: 'var(--ink2)' }}>
              {k.nachweisGueltig + k.nachweisLaeuftAb === 0
                ? <span style={{ color: 'var(--ink3)' }}>Noch keine befristeten Nachweise erfasst</span>
                : (
                  <>
                    <span><b style={{ color: 'var(--ink)' }}>{k.nachweisGueltig}</b> gültig</span>
                    <span><b style={{ color: 'var(--ink)' }}>{k.nachweisLaeuftAb}</b> läuft ab</span>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="immo-glas flex flex-col md:flex-row md:items-center" style={{ borderRadius: 26, padding: '14px 16px', gap: 14 }}>
        <span className="flex items-center whitespace-nowrap" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><ArrowsLeftRight weight="fill" size={15} />Läuft demnächst ab</span>
        <div className="flex flex-1 flex-col min-w-0 w-full" style={{ gap: 8 }}>
          {ablaeufe.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Nachweise oder Verträge laufen in den nächsten 120 Tagen ab</span>}
          {ablaeufe.map(a => {
            const f = ABLAUF_FARBE[a.status];
            const IconKomp = a.art === 'vertrag' ? Signature : SealWarning;
            return (
              <button key={`${a.id}-${a.art}-${a.text}-${a.datum}`} type="button" onClick={() => onDienstleister(a.id)}
                className="immo-hover-flaeche flex items-center min-w-0 cursor-pointer text-left"
                style={{ gap: 10, padding: '10px 12px', borderRadius: 16, border: 'none', background: 'var(--fill)', color: 'var(--ink)' }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: f.bg }}>
                  <IconKomp weight="fill" size={17} color={f.fg} />
                </span>
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{a.name}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{a.text}</span>
                </span>
                <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 800, color: f.fg }}>{deDatum(a.datum)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
